/* filepath: /Users/george/MatrizInc/MarketBrewer/Clients/street-lawyer-services/website/src/core/Pages/ProductsPage/FeaturedProductsGrid/index.tsx */
import React, { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Masonry from "react-masonry-css";
import { featuredProducts } from "./FeaturedProductsGrid.data";
import "@/styles/global/masonry.css";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const FeaturedProductsGrid: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const productRefs = useRef<(HTMLDivElement | null)[]>([]);
  const imageRefs = useRef<(HTMLImageElement | null)[]>([]);
  const scrollTriggerInstances = useRef<ScrollTrigger[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>(
    {}
  );

  // Define breakpoints for the masonry grid
  const breakpointColumnsObj = {
    default: 4,
    1100: 3,
    700: 2,
    500: 1,
  };

  // Initialize state and refs
  useEffect(() => {
    // Initialize image loading states
    const initialLoadingState: Record<string, boolean> = {};
    const initialErrorState: Record<string, boolean> = {};

    featuredProducts.forEach((product) => {
      initialLoadingState[product.id] = false;
      initialErrorState[product.id] = false;
    });

    setImagesLoaded(initialLoadingState);
    setImageErrors(initialErrorState);

    // Initialize refs array with correct length
    productRefs.current = new Array(featuredProducts.length).fill(null);
    imageRefs.current = new Array(featuredProducts.length).fill(null);
  }, []);

  // Set up animations
  useEffect(() => {
    // Store our ScrollTrigger instances to clean up only those we created
    const createdTriggers: ScrollTrigger[] = [];

    // Title animation
    if (titleRef.current) {
      const titleTrigger = ScrollTrigger.create({
        id: "featured-products-title",
        trigger: titleRef.current,
        start: "top 80%",
        once: true,
      });

      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          scrollTrigger: titleTrigger,
        }
      );

      createdTriggers.push(titleTrigger);
    }

    // Product animations
    productRefs.current.forEach((product, index) => {
      if (product) {
        const productTrigger = ScrollTrigger.create({
          id: `featured-product-${index}`,
          trigger: product,
          start: "top 90%",
          once: true,
        });

        gsap.fromTo(
          product,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            delay: 0.1 * index,
            scrollTrigger: productTrigger,
          }
        );

        createdTriggers.push(productTrigger);
      }
    });

    // Store created triggers for cleanup
    scrollTriggerInstances.current = createdTriggers;

    // Cleanup function - only kill the ScrollTrigger instances we created
    return () => {
      scrollTriggerInstances.current.forEach((trigger) => {
        if (trigger) trigger.kill();
      });
    };
  }, [imagesLoaded]);

  // Image load handler
  const handleImageLoad = (
    productId: string,
    index: number,
    img: HTMLImageElement
  ) => {
    console.log(`✅ Image loaded successfully for product: ${productId}`);

    // Store reference to the image
    imageRefs.current[index] = img;

    // Update loaded state
    setImagesLoaded((prev) => ({
      ...prev,
      [productId]: true,
    }));

    // Add smooth opacity animation
    gsap.to(img, {
      opacity: 1,
      duration: 0.5,
      delay: 0.1 * Math.min(index, 3), // Cap delay at 4 items
      ease: "power2.out",
    });

    // Get the parent container that should match image dimensions
    const container = img.closest(".masonry-image-container");
    if (container) {
      // Add appropriate height based on natural dimensions
      container.classList.add("image-loaded");
    }
  };

  // Image error handler
  const handleImageError = (productId: string, src: string) => {
    console.error(`❌ Failed to load image for product ${productId}: ${src}`);

    setImageErrors((prev) => ({
      ...prev,
      [productId]: true,
    }));
  };

  // Check if path is a GIF
  const isGif = (path: string): boolean => {
    return path.toLowerCase().endsWith(".gif");
  };

  return (
    <div ref={sectionRef} id="featured-products" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <h2
          ref={titleRef}
          className="text-3xl md:text-4xl font-bold text-center mb-12"
        >
          Featured Products
        </h2>

        {/* react-masonry-css implementation */}
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="masonry-grid"
          columnClassName="masonry-column"
        >
          {featuredProducts.map((product, index) => (
            <div
              key={product.id}
              ref={(el) => (productRefs.current[index] = el)}
              data-product-id={product.id}
              className="masonry-item"
              itemScope
              itemType="https://schema.org/Product"
            >
              <div
                className="masonry-image-container"
                style={{
                  // Use actual image aspect ratio if dimensions are available
                  paddingBottom: product.dimensions
                    ? `${
                        (product.dimensions.height / product.dimensions.width) *
                        100
                      }%`
                    : product.gridSpan?.row === 2
                    ? "100%"
                    : product.gridSpan?.row === 0.5
                    ? "50%"
                    : "75%",
                  position: "relative",
                }}
              >
                {/* Loading placeholder */}
                {!imagesLoaded[product.id] && !imageErrors[product.id] && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                )}

                {/* Error fallback */}
                {imageErrors[product.id] && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-300 p-4">
                    <svg
                      className="w-12 h-12 text-gray-500 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <p className="font-medium text-gray-700">{product.name}</p>
                  </div>
                )}

                {/* Image with proper SEO attributes */}
                {!imageErrors[product.id] &&
                  (isGif(product.image) ? (
                    <img
                      src={product.image}
                      alt={`${product.name} - ${product.category} cannabis product`}
                      ref={(el) => el && (imageRefs.current[index] = el)}
                      className="masonry-image"
                      style={{ opacity: 0 }}
                      loading="lazy"
                      fetchPriority={index < 4 ? "high" : "auto"}
                      decoding="async"
                      itemProp="image"
                      onLoad={(e) =>
                        handleImageLoad(
                          product.id,
                          index,
                          e.target as HTMLImageElement
                        )
                      }
                      onError={() =>
                        handleImageError(product.id, product.image)
                      }
                    />
                  ) : product.imageWebp ? (
                    <picture>
                      <source srcSet={product.imageWebp} type="image/webp" />
                      <img
                        src={product.image}
                        alt={`${product.name} - ${product.category} cannabis product`}
                        ref={(el) => el && (imageRefs.current[index] = el)}
                        className="masonry-image"
                        style={{ opacity: 0 }}
                        loading="lazy"
                        fetchPriority={index < 4 ? "high" : "auto"}
                        decoding="async"
                        itemProp="image"
                        onLoad={(e) =>
                          handleImageLoad(
                            product.id,
                            index,
                            e.target as HTMLImageElement
                          )
                        }
                        onError={() =>
                          handleImageError(product.id, product.image)
                        }
                      />
                    </picture>
                  ) : (
                    <img
                      src={product.image}
                      alt={`${product.name} - ${product.category} cannabis product`}
                      ref={(el) => el && (imageRefs.current[index] = el)}
                      className="masonry-image"
                      style={{ opacity: 0 }}
                      loading="lazy"
                      fetchPriority={index < 4 ? "high" : "auto"}
                      decoding="async"
                      itemProp="image"
                      onLoad={(e) =>
                        handleImageLoad(
                          product.id,
                          index,
                          e.target as HTMLImageElement
                        )
                      }
                      onError={() =>
                        handleImageError(product.id, product.image)
                      }
                    />
                  ))}
              </div>

              {/* Product information with Schema.org markup */}
              <div className="masonry-overlay">
                <p className="masonry-category" itemProp="category">
                  {product.category}
                </p>
                <h3 className="masonry-title" itemProp="name">
                  {product.name}
                </h3>
                {product.description && (
                  <meta itemProp="description" content={product.description} />
                )}
                <meta itemProp="brand" content="Street Lawyer Services" />
              </div>
            </div>
          ))}
        </Masonry>

        {/* Hidden test image to verify paths are working */}
        <div className="hidden" aria-hidden="true">
          <img
            src="/images/kief-infused-joints-inside-dispensary.jpg"
            alt="Test image"
            onLoad={() => console.log("✅ Test image loaded successfully")}
            onError={() => console.error("❌ Test image failed to load")}
          />
        </div>
      </div>
    </div>
  );
};

export default FeaturedProductsGrid;
